import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import apiConfig from '../../../constants/apiConfig';
import { FileScopeType } from './useGetAllFiles';

// Coarse split from GET /dice/files/processing-status: 'stuck' means only a
// reprocess request will index the file.
export type FileProcessingState = 'processing' | 'stuck';

// What the files list shows for a file that isn't indexed yet. 'delayed' means
// the file is queued but nothing is working the queue right now.
export type FileProcessingDisplayStatus =
  | 'queued'
  | 'processing'
  | 'delayed'
  | 'failed'
  | 'not_indexable';

export type FileProcessingEntry = {
  state: FileProcessingState;
  status: FileProcessingDisplayStatus;
  progress: number | null;
  error: string | null;
};

type ProcessingStatusResponse = {
  dataset: ({ id: string } & Partial<FileProcessingEntry> & {
      state: FileProcessingState;
    })[];
};

export const fileProcessingStatusKeys = {
  all: ['fileProcessingStatus'],
  byWorkspace: (ownerId?: string) => ['fileProcessingStatus', ownerId],
};

export const FAST_POLL_MS = 5_000;
export const SLOW_POLL_MS = 30_000;
/** Poll fast for this long after work appears, then back off. */
export const FAST_POLL_WINDOW_MS = 2 * 60 * 1000;

const ACTIVE_STATUSES: FileProcessingDisplayStatus[] = [
  'queued',
  'processing',
  'delayed',
];

export function hasActiveProcessing(
  states: Record<string, FileProcessingEntry> | undefined
): boolean {
  return Object.values(states ?? {}).some((entry) =>
    ACTIVE_STATUSES.includes(entry.status)
  );
}

/**
 * Poll every 5s for the first two minutes of activity, every 30s after that,
 * and not at all once nothing is queued or indexing (failed files wait for a
 * retry, which invalidates this query).
 */
export function processingPollInterval(
  states: Record<string, FileProcessingEntry> | undefined,
  activeSince: number | null,
  now: number
): number | false {
  if (states && !hasActiveProcessing(states)) return false;
  if (activeSince == null) return FAST_POLL_MS;
  return now - activeSince < FAST_POLL_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS;
}

export function toProcessingEntries(
  dataset: ProcessingStatusResponse['dataset']
): Record<string, FileProcessingEntry> {
  return Object.fromEntries(
    dataset.map(({ id, state, status, progress, error }) => [
      id,
      {
        state,
        status: status ?? (state === 'stuck' ? 'failed' : 'processing'),
        progress: progress ?? null,
        error: error ?? null,
      },
    ])
  );
}

/**
 * Map of notefolderId -> processing entry for every not-yet-indexed file in the
 * workspace. Only fetches while the caller reports unprocessed files, so
 * workspaces with everything indexed cost nothing.
 */
export function useFileProcessingStatus(
  ownerId?: string,
  scopeType?: FileScopeType,
  hasUnprocessedFiles?: boolean
) {
  const activeSinceRef = useRef<number | null>(null);
  return useQuery<Record<string, FileProcessingEntry>>({
    queryKey: fileProcessingStatusKeys.byWorkspace(ownerId),
    queryFn: async () => {
      const response = await axios.get<ProcessingStatusResponse>(
        `${apiConfig.apiGateway.filesBase}/processing-status`,
        {
          params: {
            workspaceId: ownerId,
            ...(scopeType ? { scopeType } : {}),
          },
        }
      );
      const entries = toProcessingEntries(response.data.dataset);
      if (!hasActiveProcessing(entries)) activeSinceRef.current = null;
      else activeSinceRef.current ??= Date.now();
      return entries;
    },
    enabled: !!ownerId && !!hasUnprocessedFiles,
    refetchInterval: (query) =>
      processingPollInterval(
        query.state.data,
        activeSinceRef.current,
        Date.now()
      ),
  });
}
